import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import Layout from "./components/Layout/Layout";
import Login from "./pages/Auth/Login";
import Landing from "./pages/Landing/Landing";
import Party from "./pages/MVP/Play";
import Match from "./pages/MVP/Match";
import Profile from "./pages/Profile/Profile";
import ProfileSettings from "./pages/Profile/ProfileSettings";
import Stats from "./pages/Stats/Stats";
import Clans from "./pages/Clans/Clans";
import Admin from "./pages/Admin/Admin";
import Subscription from "./pages/MVP/Subscription";
import Referral from "./pages/MVP/Referral";
import { captureRefFromUrl } from "./lib/referral";
import Inventory from "./pages/MVP/Skins";
import { EmailAuth, SteamCallback } from "./pages/MVP/Accounts";
import PublicProfile from "./pages/MVP/PublicProfile";
import Support from "./pages/Support/Support";
import Terms from "./pages/Public/Terms";
import Privacy from "./pages/Public/Privacy";
import Cookie from "./pages/Public/Cookie";
import PersonalDataConsent from "./pages/Public/PersonalDataConsent";
import { NotFound, Forbidden } from "./pages/System/SystemPages";
import RouteTitle from "./components/Layout/RouteTitle";
import LoadingSpinner from "./components/UI/LoadingSpinner";
import { apiFetch, safeJson } from "./lib/api";
import { mapBackendUserToApp, type BackendAuthUser } from "./lib/authUser";
import { fetchProfileStats } from "./lib/profileStats";
import { isTestAuthToken, restoreTestGuestSession } from "./lib/testAuth";
/* Без сессии: существующий защищённый раздел ведёт ко входу, несуществующий адрес остаётся 404. */
const PROTECTED = /^\/(match|profile|settings|stats|inventory|clans|subscription|referral|support|admin|lobby)(\/|$)/;
function GuestFallback() {
  const { pathname } = useLocation();
  return PROTECTED.test(pathname) ? <Navigate to="/login" replace /> : <NotFound authenticated={false} />;
}

const App: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [sessionReady, setSessionReady] = useState(false);
  const { search } = useLocation();

  useEffect(() => captureRefFromUrl(search), [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        if (useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().logout();
        }
        if (!cancelled) setSessionReady(true);
        return;
      }
      if (isTestAuthToken(token)) {
        restoreTestGuestSession();
        if (!cancelled) setSessionReady(true);
        return;
      }
      const res = await apiFetch("/auth/me");
      if (cancelled) return;
      if (!res.ok) {
        useAuthStore.getState().logout();
        setSessionReady(true);
        return;
      }
      const raw = await safeJson<BackendAuthUser>(res);
      if (!raw) {
        useAuthStore.getState().logout();
        setSessionReady(true);
        return;
      }
      const stats = await fetchProfileStats();
      useAuthStore.getState().login(mapBackendUserToApp(raw, stats));
      setSessionReady(true);
    })().catch(() => {
      if (!cancelled) setSessionReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const ping = () =>
      apiFetch("/presence", { method: "POST" }).catch(() => {});
    ping();
    const timer = setInterval(ping, 30000);
    return () => clearInterval(timer);
  }, [isAuthenticated]);

  if (!sessionReady) {
    return <LoadingSpinner />;
  }

  return (
    <>
    <RouteTitle authenticated={isAuthenticated} />
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />}
      />
      <Route
        path="/register"
        element={
          !isAuthenticated ? (
            <EmailAuth mode="register" />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/steam-callback" element={<SteamCallback />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookie-policy" element={<Cookie />} />
      <Route path="/personal-data-consent" element={<PersonalDataConsent />} />
      <Route path="/forgot-password" element={<EmailAuth mode="password" />} />
      <Route path="/reset-password" element={<EmailAuth mode="password" />} />
      {!isAuthenticated && <Route path="/" element={<Landing />} />}
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Party />} />
                <Route path="/lobby" element={<Navigate to="/" replace />} />
                <Route path="/match" element={<Navigate to="/" replace />} />
                <Route path="/match/:matchId" element={<Match />} />
                <Route path="/profile/:userId" element={<PublicProfile />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<ProfileSettings />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/clans" element={<Clans />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/referral" element={<Referral />} />
                <Route path="/support" element={<Support />} />
                <Route
                  path="/admin/*"
                  element={
                    user?.isAdmin ? <Admin /> : <Forbidden />
                  }
                />
                <Route path="*" element={<NotFound authenticated />} />
              </Routes>
            </Layout>
          ) : (
            <GuestFallback />
          )
        }
      />
    </Routes>
    </>
  );
};

export default App;
